import HttpWrapper from '@utils/http/httpWrapper'
import ShipbobAppSettings from '../../appSettings/appSetting'
import toastr from 'toastr'
import qs from 'qs'

const EXPORTER_API = ShipbobAppSettings.exporterApi
const PRODUCTS_BASE_API = ShipbobAppSettings.productsApi
const productsUrl = PRODUCTS_BASE_API + '/api/products'
const MAX_PRODUCT_SEARCH_RESULTS = 20000
const PRODUCT_NAME_OR_ID_FILTER_NAME = 'ItemNameOrInventoryIdLikeFilter'
const PRODUCT_IS_BUNDLE_FILTER = 'IsBundleFilter'
const PRODUCT_IS_ACTIVE_FILTER = 'IsActiveFilter'

class ProductServiceInternal {
  urls () {
    return {
      exportProducts: EXPORTER_API + '/api/ExportProductsRequest/filters',
      importProducts: `${productsUrl}/blobUpload`,
      upsertProduct: `${productsUrl}/upsert`,
      productsPage: ShipbobAppSettings.productV2Route,
      unmergeProduct: `${productsUrl}/unmerge`,
      getProductsByNameOrId: `${productsUrl}/filterProducts`,
      getProductsMetadataByIds: `${productsUrl}/metadata`,
      toggleProductLot: ShipbobAppSettings.photoPrint + '/api/product/ToggleProductLot',
      toggleProductDangerous: ShipbobAppSettings.photoPrint + '/api/product/ToggleHazmat',
      productCsvTemplate: `${productsUrl}/filterProductsForCsv`
    }
  }

  redirectOnError (pageToRedirectTo) {
    window.location.replace(pageToRedirectTo)
  }

  handleGetProductError (res) {
    toastr.error('Unable to retrieve product')

    if (res && res.response && res.response.status === 404) {
      this.redirectOnError(this.urls().productsPage)
    }
  }

  async exportProducts (Data) {
    const request = {
      Url: `${this.urls().exportProducts}`,
      Data
    }

    return await HttpWrapper.Post(request)
  }

  async getSearchProduct ({ filters, userId, pageSize, pageNumber }) {
    const Url = ShipbobAppSettings.productsApi + '/api/products/filterProducts'
    const Data = { filters, userId, pageSize, pageNumber }
    const request = {
      Url,
      Data
    }
    return await HttpWrapper.Post(request)
  }

  async importProducts (data) {
    const formData = new FormData()
    data.files.forEach(f => formData.append('file', f))
    const request = {
      Url: `${this.urls().importProducts}`,
      Data: formData,
      Headers: { 'Content-Type': 'multipart/form-data' }
    }
    return await HttpWrapper.Post(request, (res) => {}, ({ response }) => {
      if (response.data.message) toastr.error(response.data.message)
    }, true)
  }

  async getById (id) {
    const request = {
      Url: `${productsUrl}/${id}`
    }

    return await HttpWrapper.Get(request, undefined, this.handleGetProductError, true)
  }

  async upsertProduct (product) {
    const request = {
      Url: this.urls().upsertProduct,
      Data: product
    }

    return await HttpWrapper.Post(request, (res) => {
      toastr.success('Product information saved.')
    }, ({ response }) => {
      if (response.data.errors) {
        toastr.error(response.data.errors.join('<br/>'))
      } else {
        toastr.error('We were unable to process your request. Please try again.')
      }
    }, true)
  }

  async UnmergeProduct (Data) {
    const request = { Url: this.urls().unmergeProduct, Data }
    return await HttpWrapper.Post(request, () => {
      toastr.success(`Product ${Data.ProductToUnmergeId} was successfully unmerged.`)
    }, (res) => {
      toastr.error(res.response.data.message)
    }, true)
  }

  async EditMergeProduct (parentId, childId) {
    const request = { Url: `${productsUrl}/${parentId}/merge?childId=${childId}` }
    let response = null
    await HttpWrapper.Put(request, () => {
    }, (res) => {
      response = res.response.data.message
    }, true)
    return response
  }

  async uploadMsds (data) {
    const formData = new FormData()
    formData.append('msdsFile', data.msdsFileData)
    const request = {
      Url: `${productsUrl}/${data.productId}/msds`,
      Data: formData,
      Headers: { 'Content-Type': 'multipart/form-data' }
    }

    await HttpWrapper.Put(request, (res) => {
      toastr.success('MSDS file uploaded.')
    }, () => {
      toastr.error('We were unable to upload your MSDS file. Please try again.')
    }, true)

    return true
  }

  async uploadImage ({ image, id }) {
    const formData = new FormData()
    formData.append('image', image)
    const request = {
      Url: `${productsUrl}/${id}/image`,
      Data: formData,
      Headers: { 'Content-Type': 'multipart/form-data' }
    }

    return await HttpWrapper.Put(request, (res) => {
      toastr.success('Product image was successfully updated.')
    }, (err) => {
      toastr.error(err.data)
    }, true)
  }

  async createBundle ({ parentId, bundleChildren }) {
    const bundleChildrenForRequest = bundleChildren.map(bc => ({ Id: bc.product.id, Quantity: bc.quantity }))

    const request = { Url: `${productsUrl}/${parentId}/bundle`, Data: { ChildProducts: bundleChildrenForRequest } }
    let response = null

    await HttpWrapper.Post(request, () => {
      toastr.success('Bundle successfully created.')
    }, (res) => {
      response = res.response.data.message
      const errors = res.response.data.errors

      if (errors && errors.length > 0) {
        response += ` : ${errors.join(', ')}`
      }
    }, true)

    return response
  }

  async addToBundle ({ parentId, bundleChildren }) {
    const bundleChildrenForRequest = bundleChildren.map(bc => ({ Id: bc.product.id, Quantity: bc.quantity }))

    const request = { Url: `${productsUrl}/${parentId}/bundle/children`, Data: { BundleChildren: bundleChildrenForRequest } }
    let response = null

    await HttpWrapper.Post(request, () => {
      toastr.success('Products successfully added to the bundle.')
    }, (res) => {
      response = res.response.data.message
      const errors = res.response.data.errors

      if (errors && errors.length > 0) {
        response += ` : ${errors.join(', ')}`
      }
    }, true)

    return response
  }

  async RemoveFromBundle ({ parentId, childId }) {
    const request = { Url: `${productsUrl}/${parentId}/bundle/${childId}` }
    return await HttpWrapper.Delete(request, () => {
      toastr.success(`Product ${childId} was successfully removed from the bundle.`)
    }, (res) => {
      toastr.error(res.response.data.message)
    }, true)
  }

  async Unbundle (parentId) {
    const request = { Url: `${productsUrl}/${parentId}/bundle` }
    return await HttpWrapper.Delete(request, () => {
      toastr.success(`Product ${parentId} was successfully unbundled.`)
    }, (res) => {
      toastr.error(res.response.data.message)
    }, true)
  }

  async getProductsByNameOrId (searchTerm) {
    const request = {
      Url: this.urls().getProductsByNameOrId,
      Data: {
        PageNumber: 1,
        PageSize: MAX_PRODUCT_SEARCH_RESULTS,
        Filters: [
          {
            FilterType: PRODUCT_NAME_OR_ID_FILTER_NAME,
            Value: searchTerm
          },
          {
            FilterType: PRODUCT_IS_BUNDLE_FILTER,
            Value: 'false'
          },
          {
            FilterType: PRODUCT_IS_ACTIVE_FILTER,
            Value: 'true'
          }
        ]
      }
    }
    const result = await HttpWrapper.Post(request)
    return result.products
  }

  async getProductsMetadataByIds (ids) {
    const request = {
      Url: `${this.urls().getProductsMetadataByIds}?${qs.stringify({ productIds: ids })}`
    }
    return await HttpWrapper.Get(request)
  }

  async toggleProductLot (ids) {
    const response = null
    if (ids.length > 0) {
      const request = {
        Url: this.urls().toggleProductLot,
        Data: ids
      }

      return await HttpWrapper.Post(request, () => {}, (res) => {
        let errorMsg = res.response.data.message
        if (res.response.status === 400) errorMsg = 'Your products were updated while you were creating this WRO. Please reload the page to return the latest version of your products to submit a new WRO.'
        toastr.error(errorMsg)
      }, true)
    }
    return response
  }

  async toggleProductDangerous (ids) {
    const response = null
    if (ids.length > 0) {
      const request = {
        Url: this.urls().toggleProductDangerous,
        Data: ids
      }

      return await HttpWrapper.Post(request, () => {}, (res) => {
        let errorMsg = res.response.data.message
        if (res.response.status === 400) errorMsg = 'Your products were updated while you were creating this WRO. Please reload the page to return the latest version of your products to submit a new WRO.'
        toastr.error(errorMsg)
      }, true)
    }
    return response
  }

  async getProductCsvTemplate () {
    const request = {
      Url: this.urls().productCsvTemplate,
      ResponseType: 'blob',
      Data: {
        Filters: [
          {
            FilterType: PRODUCT_IS_BUNDLE_FILTER,
            Value: 'false'
          },
          {
            FilterType: PRODUCT_IS_ACTIVE_FILTER,
            Value: 'true'
          }
        ]
      }
    }
    const result = await HttpWrapper.Post(request, false, (response) => { throw response }, true)
    return this.convertProductsListToCsv(result)
  }

  convertProductsListToCsv (products) {
    let csv = 'InventoryId, SKU, ItemName, IsLot (Yes/No value), QuantityToSend, LotNumber, ExpirationDate' + '\n'
    const stripCommas = (str) => str.replaceAll ? str.replaceAll(',', ' ') : str

    products.forEach(product => {
      csv += `${product.id}, ${product.sku}, ${stripCommas(product.name)}, ${product.isLotProduct ? 'Yes' : 'No'},,,` + '\n'
    })

    return csv
  }
}
export const ProductService = new ProductServiceInternal()
